/**
 * Lightweight collapsible JSON tree viewer for the debug explorer.
 * Renders objects/arrays as expand/collapse nodes with syntax coloring and a
 * copy-the-whole-thing affordance. Designed for inspecting tool input/output.
 */

import { useState } from "react";
import { Copy } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const isObject = (v: unknown): v is object => v !== null && typeof v === "object";

function Primitive({ value }: { value: unknown }) {
  if (value === null)
    return <span className="text-muted-foreground">null</span>;
  if (typeof value === "string")
    return <span className="text-green-500 dark:text-green-400">{JSON.stringify(value)}</span>;
  if (typeof value === "boolean")
    return <span className="text-cyan-600 dark:text-cyan-400">{String(value)}</span>;
  if (typeof value === "number")
    return <span className="text-amber-600 dark:text-amber-400">{String(value)}</span>;
  return <span className="text-foreground">{String(value)}</span>;
}

function Node({
  k,
  value,
  depth,
  last,
}: {
  k?: string;
  value: unknown;
  depth: number;
  last: boolean;
}) {
  const [open, setOpen] = useState(depth < 2);
  const pad = { paddingLeft: depth * 14 } as const;
  const keyLabel =
    k !== undefined ? (
      <>
        <span className="text-pink-500 dark:text-pink-400">"{k}"</span>
        <span className="text-muted-foreground">: </span>
      </>
    ) : null;

  if (!isObject(value)) {
    return (
      <div style={pad} className="whitespace-pre-wrap break-words">
        {keyLabel}
        <Primitive value={value} />
        {!last && <span className="text-muted-foreground">,</span>}
      </div>
    );
  }

  const arr = Array.isArray(value);
  const entries: [string, unknown][] = arr
    ? (value as unknown[]).map((v, i) => [String(i), v])
    : Object.entries(value as Record<string, unknown>);
  const openBr = arr ? "[" : "{";
  const closeBr = arr ? "]" : "}";

  return (
    <div style={pad}>
      <span
        className="cursor-pointer select-none hover:bg-muted/40"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="text-muted-foreground">{open ? "▾ " : "▸ "}</span>
        {keyLabel}
        <span className="text-muted-foreground">
          {openBr}
          {!open && (
            <span className="text-muted-foreground/70">
              {" "}
              {entries.length} {arr ? "items" : "keys"}{" "}
              {closeBr}
            </span>
          )}
        </span>
      </span>
      {open && (
        <>
          {entries.map(([ck, cv], i) => (
            <Node
              key={ck}
              k={arr ? undefined : ck}
              value={cv}
              depth={depth + 1}
              last={i === entries.length - 1}
            />
          ))}
          <div style={pad}>
            <span className="text-muted-foreground">
              {closeBr}
              {!last && ","}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

export function JsonView({ data, className }: { data: unknown; className?: string }) {
  const copy = async () => {
    await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    toast.success("Copied to clipboard");
  };
  return (
    <div
      className={cn(
        "relative overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-[12px] leading-relaxed text-foreground",
        className,
      )}
    >
      <button
        onClick={copy}
        className="absolute right-2 top-2 z-10 flex items-center gap-1 rounded border border-border bg-background/80 px-1.5 py-0.5 text-[10px] text-muted-foreground hover:text-foreground"
      >
        <Copy className="size-3" /> copy
      </button>
      <Node value={data} depth={0} last />
    </div>
  );
}
